import {
  AdminEntryInput,
  AdminEntryPatch,
  CalendarContentType,
  CalendarDatePrecision,
  CalendarEntry,
  CalendarPlanStatus,
  CalendarRecurrence,
  CalendarScopeInput,
} from './types';
import { advertiserSummaryOf, planStatusOf, recurrenceOf } from './shared';

export interface AdminQuickAddDraft {
  entryId: string;
  creatorSlug: string;
  momentMode: 'tentpole' | 'custom';
  tentpoleSlug: string;
  customLabel: string;
  scopeMode: 'all' | 'selected';
  selectedChannelIds: string[];
  title: string;
  contentType: CalendarContentType;
  plannedDate: string;
  plannedEndDate: string;
  datePrecision: CalendarDatePrecision;
  recurrence: CalendarRecurrence;
  planStatus: CalendarPlanStatus;
  advertiserSummary: string;
  internalNote: string;
}

const scopeOf = (draft: Pick<AdminQuickAddDraft, 'scopeMode' | 'selectedChannelIds'>): CalendarScopeInput =>
  draft.scopeMode === 'all'
    ? { mode: 'all' }
    : { mode: 'selected', channelIds: [...draft.selectedChannelIds] };

/** Build the exact v2 request body sent by the Admin quick-add flow. */
export function buildAdminQuickAddPayload(draft: AdminQuickAddDraft): AdminEntryInput {
  return {
    entryId: draft.entryId,
    creatorSlug: draft.creatorSlug,
    ...(draft.momentMode === 'tentpole'
      ? { tentpoleSlug: draft.tentpoleSlug }
      : { customLabel: draft.customLabel.trim() }),
    scope: scopeOf(draft),
    title: draft.title.trim(),
    contentType: draft.contentType,
    plannedDate: draft.plannedDate,
    plannedEndDate: draft.plannedEndDate || undefined,
    datePrecision: draft.datePrecision,
    recurrence: draft.recurrence,
    planStatus: draft.planStatus,
    advertiserSummary: draft.advertiserSummary.trim() || undefined,
    internalNote: draft.internalNote.trim() || undefined,
  };
}

/**
 * The admin edit PATCH: only the fields the form changed versus `base` — the
 * entry the editor opened with, or the stored plan a create replay landed on.
 * A field the form never held (a confidential note missing from a list row)
 * is never sent back as '', which would clear it server-side. Scope goes only
 * when `scopeDirty`, so an unrelated edit never re-snapshots a page roster
 * that changed after the original submission.
 */
export function buildAdminEntryPatch(
  draft: Omit<AdminQuickAddDraft, 'entryId' | 'creatorSlug'>,
  base: CalendarEntry,
  scopeDirty: boolean,
): AdminEntryPatch {
  const patch: AdminEntryPatch = {};
  // Only the active moment field — the server deletes the other.
  if (draft.momentMode === 'tentpole') {
    if (draft.tentpoleSlug !== base.tentpoleSlug) patch.tentpoleSlug = draft.tentpoleSlug;
  } else if (draft.customLabel.trim() !== base.customLabel) {
    patch.customLabel = draft.customLabel.trim();
  }
  if (scopeDirty) patch.scope = scopeOf(draft);
  if (draft.title.trim() !== base.title) patch.title = draft.title.trim();
  if (draft.contentType !== base.contentType) patch.contentType = draft.contentType;
  if (draft.plannedDate !== base.plannedDate) patch.plannedDate = draft.plannedDate;
  // '' clears the end date (back to single-day).
  if (draft.plannedEndDate !== (base.plannedEndDate ?? '')) patch.plannedEndDate = draft.plannedEndDate;
  if (draft.datePrecision !== base.datePrecision) patch.datePrecision = draft.datePrecision;
  if (draft.recurrence !== recurrenceOf(base)) patch.recurrence = draft.recurrence;
  if (draft.planStatus !== planStatusOf(base)) patch.planStatus = draft.planStatus;
  if (draft.advertiserSummary.trim() !== advertiserSummaryOf(base)) {
    patch.advertiserSummary = draft.advertiserSummary.trim();
  }
  if (draft.internalNote.trim() !== (base.internalNote ?? '')) patch.internalNote = draft.internalNote.trim();
  return patch;
}
